
import { useState, useRef, useEffect } from 'react';
import { Address, Hex, parseEther } from 'viem';

import {
  api as blinkoApi,
  BlinkoGame,
  CreateGameRequestWithToken,
} from '../api';
import { BlinkoGameState, UseGameService } from '../types';
import { useBlinkoContract } from './useBlinkoContract';
import { promiseWithRetry } from '~/core/utils/promiseWithRetry';

import { useIsPrivyCrossApp } from '~/core/hooks/useIsPrivyCrossApp';
import { SESSION_KEYS, useAbsSession } from '~/core/contexts/AbsSessionContext';

// Utility functions
function generateSalt(): string {
  return `0x${Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')}`;
}

function validateGameParams(params: Partial<CreateGameRequestWithToken> & { betAmount?: string; hardMode?: boolean; player?: string; token?: string; v2Mode?: boolean }): { valid: boolean; error?: string } {
  if (!params.player) {
    return { valid: false, error: 'Player address required' };
  }
  if (!params.token) {
    return { valid: false, error: 'Token required' };
  }
  if (!params.betAmount || BigInt(params.betAmount) <= 0n) {
    return { valid: false, error: 'Valid bet amount required' };
  }
  return { valid: true };
}

interface UseBlinkoGameOptions {
  playerAddress: Address | null;
  defaultBetAmount?: string;
}

export function useGameService({
  playerAddress,
  defaultBetAmount = parseEther('0.001').toString(), // 0.001 ETH in wei
}: UseBlinkoGameOptions): UseGameService {
  const { isPrivyCrossApp } = useIsPrivyCrossApp();
  const { ensureSessionKey } = useAbsSession();

  // Blockchain contract integration
  const {
    createGameOnChain,
    cashOutOnChain,
    markAsLostOnChain,
    convertApiParamsToChainParams,
    calculateGameId,
    settlingGames
  } = useBlinkoContract();

  const [gameState, setGameState] = useState<BlinkoGameState>({
    currentGame: null,
    isPlaying: false,
    isCreatingGame: false,
    betAmount: defaultBetAmount,
    playerAddress,
  });

  const currentGameRef = useRef<BlinkoGame | null>(null);

  // Update player address when it changes
  useEffect(() => {
    setGameState(prev => ({ ...prev, playerAddress }));
  }, [playerAddress]);

  const internalCreateGame = async (config: CreateGameRequestWithToken): Promise<{ game: BlinkoGame; txHash: Hex; receiptPromise: Promise<void> }> => {
    if (!playerAddress) {
      throw new Error('Wallet not connected');
    }

    if (isPrivyCrossApp) {
      await ensureSessionKey(SESSION_KEYS.BLINKO);
    }

    setGameState(prev => ({
      ...prev,
      isCreatingGame: true,
      error: null
    }));

    try {
      // Generate salt and store it locally
      const salt = generateSalt();

      // Create game via API (gets server signature)
      const response = await blinkoApi.createGame(playerAddress, {
        player: config.player,
        token: config.token,
        betAmount: config.betAmount,
        hardMode: config.hardMode,
        v2Mode: config.v2Mode,
      });

      // Convert API params to blockchain params
      const chainParams = convertApiParamsToChainParams(response.gameParams, config.selectedToken);
      const gameId = calculateGameId(response.signature) as Hex;

      // Submit createGame on-chain without blocking on the receipt.
      const { txHash, receiptPromise } = await createGameOnChain({
        gameId,
        params: chainParams,
        serverSignature: response.signature,
        salt: salt as Hex,
      });

      // Create game object for local state
      const game: BlinkoGame = {
        gameId: response.gameId,
        player: playerAddress,
        token: response.gameParams.token,
        betAmount: config.betAmount,
        pinRows: 16, // Default value as it's missing in CreateGameRequestWithToken
        difficulty: 'medium', // Default value
        numDrops: 10, // Default value
        gameSeed: '', // Hidden until played
        gameSeedHash: response.gameSeedHash,
        algorithm: response.gameParams.algorithm,
        gameConfig: response.gameParams.gameConfig,
        resolver: response.gameParams.resolver,
        salt: null, // Hidden
        status: 'active',
        payoutAmount: null,
        gameState: null,
        gameStateHash: null,
        createGameSignature: response.signature,
        cashOutSignature: null,
        markAsLostSignature: null,
        deadline: response.deadline,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      currentGameRef.current = game;

      setGameState(prev => ({
        ...prev,
        currentGame: game,
        isCreatingGame: false,
        betAmount: config.betAmount,
      }));

      return { game, txHash, receiptPromise };
    } catch (error) {
      console.error('Failed to create game:', error);
      setGameState(prev => ({ ...prev, isCreatingGame: false }));
      throw error;
    }
  };

  const internalPlayGame = async (gameId: string, txHash?: string): Promise<BlinkoGame> => {
    const game = currentGameRef.current;
    if (!game || game.gameId !== gameId) {
      throw new Error('Game not found locally');
    }

    setGameState(prev => ({
      ...prev,
      isPlaying: true,
      error: null
    }));

    try {
      const response = await promiseWithRetry(() => blinkoApi.playGame(playerAddress, gameId, txHash), 3);
      const payoutAmount = response.result.totalWin;
      const isWin = BigInt(payoutAmount) > 0n;

      const updatedGame: BlinkoGame = {
        ...game,
        gameSeed: response.gameSeed,
        salt: response.salt,
        status: isWin ? 'won' : 'lost',
        payoutAmount: payoutAmount,
        gameState: response.result,
        gameStateHash: response.gameState,
        cashOutSignature: response.signatureType === 'cashOut' ? response.signature : null,
        markAsLostSignature: response.signatureType === 'markAsLost' ? response.signature : null,
        updatedAt: new Date().toISOString(),
        deadline: response.deadline,
      };

      currentGameRef.current = updatedGame;

      setGameState(prev => ({
        ...prev,
        currentGame: updatedGame,
        isPlaying: false,
      }));
      
      return updatedGame;
    } catch (error) {
      console.error('Failed to play game:', error);
      setGameState(prev => ({ ...prev, isPlaying: false }));
      throw error;
    }
  };

  const backgroundCashOut = async (game: BlinkoGame, creationReceipt?: Promise<void>) => {
    try {
      // The createGame tx was fired optimistically; wait for it to confirm
      // before settling so we don't collide with the unconfirmed creation tx.
      if (creationReceipt) {
        await creationReceipt;
      }

      const gameStateHash = game.gameStateHash as Hex;
      const gameSeedHash = game.gameSeed as Hex;
      const deadlineBigInt = BigInt(game.deadline);
      const payoutAmount = BigInt(game.payoutAmount || '0');
      const signature = game.status === 'won' ? game.cashOutSignature : game.markAsLostSignature;

      await promiseWithRetry(() => 
        game.status === 'won' 
          ? cashOutOnChain({
            gameId: game.gameId as Hex,
            payoutAmount,
            gameState: gameStateHash,
            gameSeed: gameSeedHash,
            deadline: deadlineBigInt,
            serverSignature: signature as Hex,
          }) 
          : markAsLostOnChain({
            gameId: game.gameId as Hex,
            gameState: gameStateHash,
            gameSeed: gameSeedHash,
            deadline: deadlineBigInt,
            serverSignature: signature as Hex,
          }),
        3
      );
    } catch (error) {
      console.error('Background cash out error:', error);
    }
  };

  const startGame = async (config: CreateGameRequestWithToken): Promise<BlinkoGame> => {
    // Concurrency Guard: Prevent starting a new game if one is already active or being processed
    if (gameState.isCreatingGame || gameState.isPlaying) {
      throw new Error('Cannot start new game: Another operation in progress');
    }
    // Check if there is an active game. 
    // Since internalPlayGame now sets status to won/lost, this check correctly blocks 
    // only if the previous game wasn't even played properly (stuck in 'active').
    if (gameState.currentGame && gameState.currentGame.status === 'active') {
       throw new Error('Cannot start new game: A game is already active.');
    }

    const { game, txHash, receiptPromise } = await internalCreateGame(config);
    const playedGame = await internalPlayGame(game.gameId, txHash);
    backgroundCashOut(playedGame, receiptPromise);

    return playedGame;
  };

  const resumeGame = async (gameId: string): Promise<BlinkoGame> => {
    // Concurrency Guard
    if (gameState.isCreatingGame || gameState.isPlaying) {
       throw new Error('Cannot resume game: Another operation in progress');
    }
    // If we are already in this game session, just return current
    if (gameState.currentGame && gameState.currentGame.gameId === gameId) {
       return gameState.currentGame;
    }
    // If we have ANOTHER active game, block resume?
    if (gameState.currentGame && gameState.currentGame.status === 'active' && gameState.currentGame.gameId !== gameId) {
        throw new Error('Cannot resume different game: A game is already active.');
    }

    try {
      const game = await blinkoApi.getGame(gameId);
      if (!game) throw new Error('Game not found');
      
      currentGameRef.current = game;
      setGameState(prev => ({
        ...prev,
        currentGame: game,
      }));

      const playedGame = await internalPlayGame(gameId);
      backgroundCashOut(playedGame);

      return playedGame;
    } catch (e) {
      console.error('Failed to resume session', e);
      throw e;
    }
  };

  const replayGame = async (gameId: string): Promise<BlinkoGame> => {
    try {
      // 1. Fetch Game
      const game = await blinkoApi.getGame(gameId);
      if (!game) throw new Error('Game not found');

      // 2. Set as current locally
      currentGameRef.current = game;
      setGameState(prev => ({
        ...prev,
        currentGame: game,
        // Ensure not playing or creating
        isPlaying: false,
        isCreatingGame: false,
      }));
      
      return game;
    } catch (e) {
       console.error('Failed to load replay', e);
       throw e;
    }
  };

  return {
    settlingGames: settlingGames,
    startGame,
    resumeGame,
    replayGame,
  } as UseGameService;
}
